export interface ProjectStatus {
  projectName: string;
  projectSlug: string;
  recommendation: string;
  leadConcept: string;
  fallbackConcept: string;
  contentState: string;
  environment: "staging" | "production";
  lastPublishedAt: string;
}

export interface RecommendationData {
  label: string;
  confidencePosture: string;
  why: string[];
  topPositives: string[];
  topCautions: string[];
}

export interface ArtifactNavItem {
  section_slug: string;
  section_name: string;
  route: string;
  output_filename: string;
  source_rel_path: string;
  object_key: string;
}

export interface ArtifactNav {
  projectName: string;
  projectSlug: string;
  items: ArtifactNavItem[];
}

export interface OverviewData {
  projectName: string;
  projectSlug: string;
  recommendation: string;
  leadConcept: string;
  fallbackConcept: string;
  executiveSummary: string;
  topStrengths: string[];
  topCautions: string[];
  lastUpdated: string;
  memoRoute: string;
}

export interface KeyFactsData {
  propertyAddress: string;
  parcelId: string;
  zoning: string;
  parcelArea: { acres: number; squareFeet: number };
  frontageFeet: number;
  depthFeet: number;
  propertyClass: string;
  cityWaterAvailable: boolean;
  citySewerAvailable: boolean;
  offStreetParkingRequired: boolean;
  leadConcept: string;
  fallbackConcept: string;
}

export interface SiteSummaryData {
  overallPosture: string;
  floodStatus: { label: string; summary: string; confidence: string; surveyCertified: boolean };
  slopeStatus: { label: string; summary: string; confidence: string };
  utilityStatus: { label: string; summary: string; citySideBaseline: number };
  parkingAccessStatus: { label: string; summary: string };
  gisFlagStatus: { label: string; summary: string };
  openItems: string[];
  leadConceptFit: string;
}

export interface CostSummaryData {
  costPosture: string;
  utilityBaseline: { citySideKnownBaseline: number; summary: string };
  strongerInputs: string[];
  openCostRisks: string[];
  parkingCostBurdenRequired: boolean;
  leadConceptCostFit: string;
}

export interface OperationsSummaryData {
  operatingPosture: string;
  registrationRequired: boolean;
  registrationFee: number;
  registrationPenalty: number;
  lodgingTax: { percent: number; perRoomNight: number };
  virginiaAccommodationsTaxApplies: boolean;
  businessLicensePosture: string;
  businessPersonalProperty: { applies: boolean; dueDate: string; ratePer100: number };
  openOperatingItems: string[];
}

export interface RiskSummaryData {
  recommendation: string;
  riskPosture: string;
  whatImproved: string[];
  remainingOpenRisks: string[];
  gateSummary: { legal: string; site: string; revenue: string; riskAdjustedReturn: string };
  killCriteriaSensitivity: string[];
}

export interface MemoSummaryData {
  title: string;
  recommendation: string;
  leadConcept: string;
  fallbackConcept: string;
  summary: string;
  strengths: string[];
  cautions: string[];
  markdownKey: string;
}

export interface ParcelSummaryData {
  title: string;
  parcelOverview: string;
  keyFacts: {
    propertyAddress: string;
    parcelId: string;
    legalDescription: string;
    propertyClass: string;
    zoning: string;
    zoningDescription: string;
    county: string;
    coordinates: string;
    parcelArea: { acres: number; squareFeet: number };
    frontageFeet: number;
    depthFeet: number;
    taxAssessedValue: number;
  };
  highlights: string[];
  openItems: string[];
  conclusion: string;
  status: string;
}

export interface ZoningSummaryData {
  title: string;
  regulatoryOverview: string;
  keyFacts: {
    propertyAddress: string;
    parcelId: string;
    zoning: string;
    zoningDescription: string;
    legalDescriptionReference: string;
    rentalInspectionDistrictStatus: string;
    strRegistrationRequired: boolean;
    strRegistrationFee: number;
    failureToRegisterPenalty: number;
    occupancyRule: string;
    relatedOccupancyRule: string;
    offStreetParkingRequired: boolean;
    buildingCodeRequirement: string;
    inspectionApproach: string;
  };
  regulatoryTakeaways: string[];
  openItems: string[];
  parcelApplicationBestRead: string;
  conclusion: string;
  status: string;
}

export interface DemandSummaryData {
  title: string;
  locationOverview: string;
  demandDrivers: Array<{ demandDriver: string; whyItMatters: string }>;
  guestSegments: string[];
  strengthAssessment: Array<{ category: string; currentRead: string; notes: string }>;
  risks: string[];
  currentDemandRead: string;
  conclusion: string;
  status: string;
}

export interface CompsSummaryData {
  title: string;
  overview: string;
  compCategories: Array<{ compCategory: string; whatItShows: string; usefulness: string }>;
  strongestComps: string[];
  supports: Array<{ topic: string; conclusion: string; confidence: string }>;
  limits: string[];
  currentCompRead: string;
  conclusion: string;
  status: string;
}

export interface CompsTableData {
  title: string;
  referenceSet: Array<{
    compName: string;
    productType: string;
    capacitySignal: string;
    locationLogic: string;
    notableSignal: string;
    relevance: string;
  }>;
  strongestFunctionalComps: string[];
  strongestDemandSignalComps: string[];
  secondaryPositioningComps: string[];
  supportTopics: Array<{ topic: string; currentBestRead: string; confidence: string }>;
  limitations: string[];
  earlyInterpretation: string;
  status: string;
}

export interface FStModelSummaryData {
  title: string;
  overview: string;
  referenceProperties: Array<{
    address: string;
    saleSignal: string;
    size: string;
    bedroomsBaths: string;
    buildYear: string;
    keyImplication: string;
  }>;
  takeaways: string[];
  limits: string[];
  currentInterpretation: string;
  conclusion: string;
  status: string;
}

export interface ConceptSummaryData {
  title: string;
  executiveRecommendation: string;
  leadConcept: {
    concept: string;
    corePositioning: string;
    bestFitGuestThesis: string;
    whyItLeads: string;
    currentCompSupport: string;
    currentRole: string;
  };
  whyLead: string[];
  fallbackConcepts: Array<{
    concept: string;
    role: string;
    whyItStaysAlive: string;
    whyItDoesNotLead: string;
  }>;
  assumptions: string[];
  tests: string[];
  currentRecommendationRead: string;
  conclusion: string;
  status: string;
}

export interface ConceptComparisonData {
  title: string;
  conceptOptions: Array<{
    concept: string;
    description: string;
    whyItFits: string;
    mainRisk: string;
    currentRank: string;
  }>;
  comparisonMatrix: Array<{
    evaluationTopic: string;
    leadConcept: string;
    largerCompact: string;
    twoUnits: string;
    novelty: string;
  }>;
  positioningOptions: Array<{
    positioningStrategy: string;
    coreGuest: string;
    whyItFits: string;
    mainRisk: string;
    currentStrength: string;
  }>;
  currentBestRead: string;
  positioningBestRead: string;
  limitations: string[];
  status: string;
}

export interface UnderwritingSummaryData {
  title: string;
  overview: string;
  corePosition: Array<{ topic: string; currentRead: string }>;
  strSupport: string[];
  fallbackSupport: string[];
  marketBackdrop: Array<{ metric: string; currentRead: string; sourceLogic: string; confidence: string }>;
  operatingConsiderations: Array<{ operatingTopic: string; currentRead: string; whyItMatters: string }>;
  disciplineRules: Array<{ rule: string; whyItExists: string }>;
  limits: string[];
  currentBestRead: string;
  conclusion: string;
  status: string;
}

export interface FallbackSummaryData {
  title: string;
  overview: string;
  fallbackPaths: Array<{ fallbackPath: string; currentBestRead: string; roleInProject: string }>;
  supportBullets: string[];
  longTermRentalRead: string;
  buildToSellRead: string;
  processedFallbackPaths: Array<{ fallbackPath: string; currentBestRead: string; whyItMatters: string; confidence: string }>;
  limits: string[];
  currentBestRead: string;
  conclusion: string;
  status: string;
}

export interface ArtifactReferenceData {
  path: string;
  section: string;
  availability: "readable-in-working-snapshot" | "listed-in-approved-manifest" | "missing-from-approved-manifest";
  previewHeading?: string;
}

export interface WorkingSnapshotCoverageData {
  readableCount: number;
  manifestOnlyCount: number;
  missingCount: number;
}

export interface ContractedFieldGroupData {
  name: string;
  currentRead: string;
  status: "current-public-data" | "readable-promoted-source" | "manifest-backed-pending-source-body";
  sourceArtifacts: string[];
}

export interface ContractedPublicObjectData {
  title: string;
  targetSurface: string;
  contractRole: string;
  currentRuntimeCompatibility: string[];
  backingArtifacts: ArtifactReferenceData[];
  workingSnapshotCoverage: WorkingSnapshotCoverageData;
  fieldGroups: ContractedFieldGroupData[];
  disclosures: string[];
}

export type FinancialInformationClass =
  | "verified-fact"
  | "modeled-assumption"
  | "derived-output"
  | "unresolved-item";

export interface FinancialDatumData {
  label: string;
  value: string;
  informationClass: FinancialInformationClass;
  statusTag?: string;
  numericValue?: number;
  unit?: "usd" | "percent" | "count" | "text";
  notes?: string;
  sourceArtifacts: string[];
}

export interface FinancialTableGroupData {
  title: string;
  columns: string[];
  rows: Array<Record<string, string>>;
  sourceArtifacts: string[];
}

export interface FinancialSectionData extends ContractedPublicObjectData {
  objectKey: string;
  summary: string;
  supportingObjectKeys: string[];
  supportingRoutes: string[];
  verifiedFacts: FinancialDatumData[];
  modeledAssumptions: FinancialDatumData[];
  derivedOutputs: FinancialDatumData[];
  unresolvedItems: FinancialDatumData[];
  tableGroups: FinancialTableGroupData[];
  disclosureNotes: string[];
  statusTagDefinitions?: Array<{ tag: string; meaning: string }>;
}

export interface SourcesSummaryData {
  title: string;
  summary: string;
  methodologyNotes: string[];
  sourceCategoryCounts: Array<{ category: string; count: number }>;
  reliabilityBreakdown: Array<{ label: string; count: number }>;
  sectionCoverage: string[];
  representativeSources: Array<{
    name: string;
    type: string;
    reference: string;
    reliability: string;
    sectionsSupported: string[];
  }>;
  limitations: string[];
}

export interface CompsHubData {
  compsSummary: CompsSummaryData;
  compsTable: CompsTableData;
  precedentSummary: FStModelSummaryData;
  conceptSummary: ConceptSummaryData;
  demandSummary: DemandSummaryData;
  underwritingSummary: UnderwritingSummaryData;
  memoSummary: MemoSummaryData;
  sourcesSummary: SourcesSummaryData;
}

export interface FinancialsData {
  projectFacts: KeyFactsData & {
    utilityBaselineUsd?: number;
    utilityBaselineSummary?: string;
    registrationFeeUsd?: number;
    registrationPenaltyUsd?: number;
    lodgingTaxPercent?: number;
    lodgingTaxPerRoomNightUsd?: number;
  };
  lineage: {
    sourceAuthority: string;
    objectKeys: {
      developmentBudget: string;
      costBreakdown: string;
      operatingModel: string;
      underwritingScenarios: string;
      fallbackCase: string;
      sensitivities: string;
      assumptionsAndDisclosures: string;
    };
    supportingRoutes: Record<string, string>;
  };
  informationClasses: Array<{
    id: FinancialInformationClass;
    label: string;
    description: string;
  }>;
  developmentBudget: FinancialSectionData;
  costBreakdown: FinancialSectionData;
  operatingModel: FinancialSectionData;
  underwritingScenarios: FinancialSectionData;
  fallbackCase: FinancialSectionData;
  sensitivities: FinancialSectionData;
  assumptionsAndDisclosures: FinancialSectionData;
}

async function expectOk(response: Response): Promise<Response> {
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed with ${response.status}`);
  }

  return response;
}

async function fetchJson<T>(path: string): Promise<T> {
  const response = await expectOk(await fetch(path, { headers: { accept: "application/json" } }));
  return response.json() as Promise<T>;
}

async function fetchText(path: string): Promise<string> {
  const response = await expectOk(await fetch(path, { headers: { accept: "text/markdown, text/plain" } }));
  return response.text();
}

export const api = {
  getStatus: () => fetchJson<ProjectStatus>("/api/status"),
  getRecommendation: () => fetchJson<RecommendationData>("/api/recommendation"),
  getNav: () => fetchJson<ArtifactNav>("/api/nav"),
  getOverview: () => fetchJson<OverviewData>("/api/overview"),
  getParcel: () => fetchJson<ParcelSummaryData>("/api/parcel"),
  getZoning: () => fetchJson<ZoningSummaryData>("/api/zoning"),
  getSite: () => fetchJson<{ siteSummary: SiteSummaryData; keyFacts: KeyFactsData }>("/api/site"),
  getDemand: () => fetchJson<DemandSummaryData>("/api/demand"),
  getComps: () => fetchJson<{ compsSummary: CompsSummaryData; compsTable: CompsTableData }>("/api/comps"),
  getPrecedent: () => fetchJson<FStModelSummaryData>("/api/precedent"),
  getConcept: () => fetchJson<{ conceptSummary: ConceptSummaryData; conceptComparison: ConceptComparisonData }>("/api/concept"),
  getEconomics: () =>
    fetchJson<{ costSummary: CostSummaryData; operationsSummary: OperationsSummaryData; keyFacts: KeyFactsData }>(
      "/api/economics"
    ),
  getFinancials: () => fetchJson<FinancialsData>("/api/financials"),
  getDevelopmentBudget: () => fetchJson<FinancialSectionData>("/api/financials/development-budget"),
  getCostBreakdown: () => fetchJson<FinancialSectionData>("/api/financials/cost-breakdown"),
  getOperatingModel: () => fetchJson<FinancialSectionData>("/api/financials/operating-model"),
  getUnderwriting: () => fetchJson<UnderwritingSummaryData>("/api/underwriting"),
  getUnderwritingScenarios: () => fetchJson<FinancialSectionData>("/api/financials/underwriting-scenarios"),
  getSensitivityAnalysis: () => fetchJson<FinancialSectionData>("/api/financials/sensitivity-analysis"),
  getAssumptionsRegister: () => fetchJson<FinancialSectionData>("/api/financials/assumptions-register"),
  getFallback: () => fetchJson<FallbackSummaryData>("/api/fallback"),
  getFallbackEconomics: () => fetchJson<FinancialSectionData>("/api/financials/fallback-economics"),
  getRisk: () => fetchJson<{ riskSummary: RiskSummaryData; recommendation: RecommendationData }>("/api/risk"),
  getSources: () => fetchJson<SourcesSummaryData>("/api/sources"),
  getCompsHub: async (): Promise<CompsHubData> => {
    const [comps, precedentSummary, concept, demandSummary, underwritingSummary, memoSummary, sourcesSummary] =
      await Promise.all([
        fetchJson<{ compsSummary: CompsSummaryData; compsTable: CompsTableData }>("/api/comps"),
        fetchJson<FStModelSummaryData>("/api/precedent"),
        fetchJson<{ conceptSummary: ConceptSummaryData; conceptComparison: ConceptComparisonData }>("/api/concept"),
        fetchJson<DemandSummaryData>("/api/demand"),
        fetchJson<UnderwritingSummaryData>("/api/underwriting"),
        fetchJson<MemoSummaryData>("/api/memo-summary"),
        fetchJson<SourcesSummaryData>("/api/sources")
      ]);

    return {
      compsSummary: comps.compsSummary,
      compsTable: comps.compsTable,
      precedentSummary,
      conceptSummary: concept.conceptSummary,
      demandSummary,
      underwritingSummary,
      memoSummary,
      sourcesSummary
    };
  },
  getMemoSummary: () => fetchJson<MemoSummaryData>("/api/memo-summary"),
  getMemo: () => fetchText("/api/memo")
};
